/**
 * /me/loans/applications — Session 3d PR 14.
 * Owning agent: Khidmat.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3dApi } from "../lib/session3d-api";
import type { LoanApplicationItem } from "../lib/session3d-api";
import { MeListPage } from "./_MeListPage";
import type { MeColumn } from "./_MeListPage";
import { useCurrentEmployeeId } from "./_session3dShell";

export function MyLoanApplicationsPage() {
  const { t } = useTranslation();
  const employeeId = useCurrentEmployeeId();

  const query = useQuery({
    queryKey: ["s3d-loan-applications", employeeId],
    queryFn: () => session3dApi.listMyLoanApplications(employeeId),
    enabled: !!employeeId,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: MeColumn<LoanApplicationItem>[] = [
    {
      headerKey: "me.loan-applications.col.product",
      label: "Product",
      render: (r) => <span className="font-medium">{r.product_name_en}</span>,
    },
    {
      headerKey: "me.loan-applications.col.amount",
      label: "Amount (SAR)",
      render: (r) => <span className="font-mono">{r.amount_sar.toLocaleString()}</span>,
    },
    {
      headerKey: "me.loan-applications.col.tenure",
      label: "Tenure",
      render: (r) => <span className="font-mono text-xs">{r.tenure_months}mo</span>,
    },
    {
      headerKey: "me.loan-applications.col.state",
      label: "State",
      render: (r) => <span className="text-xs">{r.state}</span>,
    },
    {
      headerKey: "me.loan-applications.col.submitted",
      label: "Submitted",
      render: (r) => <span dir="ltr" className="text-xs text-text-secondary">{r.submitted_at}</span>,
    },
  ];

  return (
    <MeListPage<LoanApplicationItem>
      title={t("me.loan-applications.title", "My Loan Applications")}
      subtitle={t("me.loan-applications.subtitle", "Loan application history with workflow states.")}
      domain="loan-applications"
      khidmatPrefill={t("me.loan-applications.ask_khidmat", "What's the status of my pending loan applications?")}
      query={query}
      columns={columns}
      rowKey={(r) => r.application_id}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("me.loan-applications.title", "My Loan Applications"),
        window_label: t("me.loan-applications.window", "across products"),
      }}
      emptyHeadline={t("me.loan-applications.empty_headline", "No loan applications yet.")}
      emptyBody={t("me.loan-applications.empty_body", "Khidmat: when you apply for a loan, your applications appear here.")}
    />
  );
}
